/**
 * Three-point editing in the editor (1.7.4, docs/dev/editor-1.7.4-build.md "Track targeting"): what the source
 * viewer's Insert (,) and Overwrite (.) put where, and match frame (F). Pure: the marks resolve with the contracts'
 * resolveThreePoint, the targets with resolveTargets, and the edit itself is ops.overwriteEdit / ops.insertEdit.
 */
import {
  allAudioTracks, clipDuration, ops, resolveAt, resolveTargets, resolveThreePoint,
  type EditResult, type EditTargets, type ItemRef, type RippleMode, type SequenceDocument, type SourceRange, type ThreePointEdit,
} from '@prism/contracts';

/** What the source viewer has loaded: its range (defaults filled in), its marks as set (null: not set) and its kind. */
export interface SourceMarks {
  range: SourceRange;
  /** Source ticks; null when the mark is not set. */
  sourceIn: number | null;
  sourceOut: number | null;
  /** The source's length in ticks (0: a still or not known). */
  duration: number;
  kind: 'video' | 'audio' | 'image' | null;
}

export type ThreePointKind = 'insert' | 'overwrite';

/** A planned edit: the resolved marks, the source range to place and the targets that apply. */
export interface ThreePointPlan { edit: ThreePointEdit; src: SourceRange; targets: EditTargets }

/** The words for a plan that cannot be made (the editor's error toast). */
export const NO_SOURCE = 'Pick media in the bin first';
export const NO_RANGE = 'Mark a range with media in it';
export const SHORTENED = 'The source is shorter than In to Out';

/**
 * The targets for a source of this kind: a sound file has no picture (the sound goes to the targeted audio track, or
 * A2 when the target is the picture's own sound), a still has no sound.
 */
export function targetsForSource(targets: EditTargets, kind: SourceMarks['kind']): EditTargets {
  if (kind === 'audio') return { video: null, audio: targets.audio && targets.audio !== 'with-picture' ? targets.audio : targets.audio === null ? null : { track: 'a2' } };
  if (kind === 'image') return { video: targets.video, audio: null };
  return targets;
}

/**
 * Resolve the source and sequence marks and the playhead into one edit (the three-point rule), or the reason there is
 * none. A still has no length: without a sequence In and Out it gets its source range (the viewer's default length).
 */
export function planThreePoint(doc: SequenceDocument, source: SourceMarks | null, playhead: number, targetsPref: EditTargets | null | undefined): ThreePointPlan | { problem: string } {
  if (!source) return { problem: NO_SOURCE };
  const still = source.kind === 'image';
  const seqIn = doc.marks?.inTicks ?? null;
  const seqOut = doc.marks?.outTicks ?? null;
  const both = seqIn !== null && seqOut !== null;
  const edit = resolveThreePoint({
    sourceIn: still ? (both ? null : source.range.sourceInTicks) : source.sourceIn,
    sourceOut: still ? (both ? null : source.range.sourceOutTicks) : source.sourceOut,
    seqIn, seqOut, sourceDuration: still ? 0 : source.duration, playhead,
  });
  if (!edit) return { problem: NO_RANGE };
  const src: SourceRange = { ...source.range, sourceInTicks: edit.sourceInTicks, sourceOutTicks: edit.sourceOutTicks };
  return { edit, src, targets: targetsForSource(resolveTargets(doc, targetsPref), source.kind) };
}

/** Make the planned edit (ops; 'sync' ripples the other tracks on an insert). */
export function runThreePoint(doc: SequenceDocument, plan: ThreePointPlan, kind: ThreePointKind, ripple: RippleMode): EditResult {
  return kind === 'overwrite'
    ? ops.overwriteEdit(doc, plan.src, plan.edit.recordInTicks, plan.targets)
    : ops.insertEdit(doc, plan.src, plan.edit.recordInTicks, plan.targets, { ripple });
}

/* ---------------------------------------------------------------- match frame (F) */

/** Where match frame opens the source viewer: the clip's asset at the source frame under the playhead, with its In and Out. */
export interface MatchFrame { assetId: string; clipId: string; sourceTicks: number; inTicks: number; outTicks: number }

/**
 * The clip match frame opens: the selected layer or audio clip when it is under the playhead, else the V1 clip there
 * (resolveAt), else null.
 */
export function matchFrameAt(doc: SequenceDocument, playhead: number, primary: ItemRef | null): MatchFrame | null {
  if (primary && (primary.kind === 'layer' || primary.kind === 'audio')) {
    const c = primary.kind === 'layer'
      ? (doc.layers ?? []).flatMap((l) => l.clips).find((x) => x.id === primary.id)
      : allAudioTracks(doc).flatMap((t) => t.clips).find((x) => x.id === primary.id);
    if (c && playhead >= c.recordInTicks && playhead < c.recordInTicks + clipDuration(c)) {
      return { assetId: c.assetId, clipId: c.id, sourceTicks: c.sourceInTicks + (playhead - c.recordInTicks), inTicks: c.sourceInTicks, outTicks: c.sourceOutTicks };
    }
  }
  const hit = resolveAt(doc, playhead);
  if (!hit) return null;
  const c = hit.placed.clip;
  return { assetId: c.assetId, clipId: c.id, sourceTicks: hit.sourceTicks, inTicks: c.sourceInTicks, outTicks: c.sourceOutTicks };
}
